import { ref, watch, computed, isRef } from "vue";
import { useI18n } from "vue-i18n";
import { getAvailableActions } from "@/entities/transaction/api";
import { pickLabel } from "@/shared/lib/pickLabel";

function readSource(source) {
  if (typeof source === "function") return source();
  if (isRef(source)) return source.value;
  return source || {};
}

function normalizeId(value) {
  if (value === undefined || value === null || value === "") return null;
  return value;
}

export function useAvailableActions(source) {
  const { locale } = useI18n();

  const raw = ref(null);
  const loading = ref(false);
  const error = ref(null);

  const root = computed(() => raw.value?.payload || raw.value || null);

  const availableActions = computed(() => {
    const items = root.value?.primary?.available_actions || [];
    return items.map((a) => ({
      code: a.code,
      label: pickLabel(a.label, locale.value),
      raw: a,
    }));
  });

  const currentStatus = computed(
    () => root.value?.primary?.current_status || null
  );

  async function refresh() {
    const src = readSource(source);
    const transactionId = normalizeId(src.transactionId);
    const applicationUuid = normalizeId(src.applicationUuid);

    if (!transactionId && !applicationUuid) {
      raw.value = null;
      error.value = null;
      return;
    }

    loading.value = true;
    error.value = null;
    try {
      const data = await getAvailableActions({
        transactionId,
        applicationUuid,
      });
      if (data?.success === false) {
        error.value = data.error || "Не удалось получить доступные действия";
        raw.value = null;
      } else {
        raw.value = data || null;
      }
    } catch (e) {
      error.value = e?.message || String(e);
      raw.value = null;
    } finally {
      loading.value = false;
    }
  }

  watch(
    () => {
      const src = readSource(source);
      return [normalizeId(src.transactionId), normalizeId(src.applicationUuid)];
    },
    refresh,
    { immediate: true }
  );

  return {
    availableActions,
    currentStatus,
    loading,
    error,
    refresh,
    raw,
  };
}
